import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { takedownApi } from "core/api";
import { AutoPolicy } from "core/types/takedown.types";

type ChipColor = "default" | "success" | "warning" | "error" | "info";

/**
 * Pannello Auto-Policy (G1/G2). Legge/scrive GET/PUT /admin/takedown/policy.
 * - Auto-select: findings coerenti (≥ soglia) + non-omonimi + negativi → pratica auto.
 * - Auto-approve: solo non-giudiziali e severity < 4; il kill-switch resta sovrano sull'invio.
 * Kill-switch (send_enabled) e SMTP-verify sono mostrati SOLO in lettura (env, gestiti dall'Integrator).
 */
export const AutoPolicyPanel: React.FC = () => {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState<AutoPolicy | null>(null);
  const [draft, setDraft] = useState<AutoPolicy | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [howTo, setHowTo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await takedownApi.getPolicy();
      setPolicy(p);
      setDraft(p);
    } catch (e: any) {
      setError(t("takedown.autopolicy.error_load", { error: e?.message || e }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof AutoPolicy>(k: K, v: AutoPolicy[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const dirty =
    !!policy &&
    !!draft &&
    (["autoselect_enabled", "autoselect_min_coherence", "autoapprove_enabled", "autoapprove_max_severity"] as const).some(
      (k) => policy[k] !== draft[k]
    );

  const save = async () => {
    if (!policy || !draft) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Invia solo i campi modificabili (kill-switch/SMTP-verify sono read-only lato UI).
      const patch: Partial<AutoPolicy> = {
        autoselect_enabled: draft.autoselect_enabled,
        autoselect_min_coherence: draft.autoselect_min_coherence,
        autoapprove_enabled: draft.autoapprove_enabled,
        autoapprove_max_severity: draft.autoapprove_max_severity,
      };
      const next = await takedownApi.updatePolicy(patch);
      setPolicy(next);
      setDraft(next);
      setSaved(true);
    } catch (e: any) {
      setError(t("takedown.autopolicy.error_save", { error: e?.message || e }));
    } finally {
      setSaving(false);
    }
  };

  const roChip = (on: boolean, onLabel: string, offLabel: string, onColor: ChipColor = "success") => (
    <Chip
      size="small"
      color={on ? onColor : "default"}
      variant={on ? "filled" : "outlined"}
      icon={<LockOutlinedIcon fontSize="small" />}
      label={on ? onLabel : offLabel}
    />
  );

  return (
    <Paper sx={{ borderRadius: "10px", p: "20px", mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <TuneRoundedIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          {t("takedown.autopolicy.title")}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t("takedown.autopolicy.desc_1")}<b>{t("takedown.autopolicy.desc_bold")}</b>{t("takedown.autopolicy.desc_2")}
      </Typography>

      {/* I due interruttori agiscono su due punti diversi di una catena di cinque passi, e senza
          dirlo non si capisce cosa cambia accendendoli. Il box e' richiudibile: serve la prima
          volta, non tutti i giorni. */}
      <Button
        size="small"
        startIcon={<HelpOutlineRoundedIcon />}
        endIcon={howTo ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        onClick={() => setHowTo((v) => !v)}
        sx={{ mb: 1 }}
      >
        {t("takedown.autopolicy.howto_toggle")}
      </Button>
      <Collapse in={howTo}>
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t("takedown.autopolicy.howto_title")}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
            {["step_1", "step_2", "step_3", "step_4", "step_5"].map((k) => (
              <li key={k}>
                <Typography variant="body2">{t(`takedown.autopolicy.howto_${k}`)}</Typography>
              </li>
            ))}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <b>{t("takedown.autopolicy.howto_note_label")}</b> {t("takedown.autopolicy.howto_note")}
          </Typography>
        </Alert>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Collapse in={saved}>
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
          {t("takedown.autopolicy.saved")}
        </Alert>
      </Collapse>

      {loading || !draft ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} color="secondary" />
        </Box>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems="flex-start"
            flexWrap="wrap"
            useFlexGap
          >
            {/* Ogni comando porta sotto la frase che dice cosa succede acceso e cosa spento:
                il tooltip da solo non basta, va scoperto passandoci sopra. */}
            <Box sx={{ maxWidth: 340 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.autoselect_enabled}
                    onChange={(e) => set("autoselect_enabled", e.target.checked)}
                  />
                }
                label={t("takedown.autopolicy.autoselect_label")}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {draft.autoselect_enabled
                  ? t("takedown.autopolicy.autoselect_help_on")
                  : t("takedown.autopolicy.autoselect_help_off")}
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 300 }}>
              <Tooltip title={t("takedown.autopolicy.coherence_tooltip")}>
                <TextField
                  size="small"
                  type="number"
                  label={t("takedown.autopolicy.coherence_label")}
                  value={draft.autoselect_min_coherence}
                  onChange={(e) =>
                    set(
                      "autoselect_min_coherence",
                      Math.max(0, Math.min(100, Number(e.target.value) || 0))
                    )
                  }
                  disabled={!draft.autoselect_enabled}
                  inputProps={{ min: 0, max: 100, step: 5 }}
                  sx={{ width: 170 }}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t("takedown.autopolicy.coherence_help")}
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 340 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.autoapprove_enabled}
                    onChange={(e) => set("autoapprove_enabled", e.target.checked)}
                  />
                }
                label={t("takedown.autopolicy.autoapprove_label")}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {draft.autoapprove_enabled
                  ? t("takedown.autopolicy.autoapprove_help_on")
                  : t("takedown.autopolicy.autoapprove_help_off")}
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 300 }}>
              <FormControl size="small" sx={{ width: 200 }} disabled={!draft.autoapprove_enabled}>
                <InputLabel id="max-sev-label">{t("takedown.autopolicy.max_severity_label")}</InputLabel>
                <Select
                  labelId="max-sev-label"
                  label={t("takedown.autopolicy.max_severity_label")}
                  value={draft.autoapprove_max_severity}
                  onChange={(e) =>
                    set("autoapprove_max_severity", Number(e.target.value) as AutoPolicy["autoapprove_max_severity"])
                  }
                >
                  {[0, 1, 2, 3].map((s) => (
                    <MenuItem key={s} value={s}>
                      {t("takedown.autopolicy.tier", { tier: s })} {s === 3 ? t("takedown.autopolicy.tier_max_suffix") : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t("takedown.autopolicy.max_severity_help")}
              </Typography>
            </Box>
          </Stack>

          {draft.autoapprove_enabled && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t("takedown.autopolicy.autoapprove_warning")}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title={t("takedown.autopolicy.env_status_tooltip")}>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: "help" }}>
                {t("takedown.autopolicy.env_status_label")}
              </Typography>
            </Tooltip>
            <Tooltip title={t("takedown.autopolicy.killswitch_tooltip")}>
              {roChip(draft.send_enabled, t("takedown.autopolicy.send_enabled_on"), t("takedown.autopolicy.send_enabled_off"), "error")}
            </Tooltip>
            <Tooltip title={t("takedown.autopolicy.smtp_tooltip")}>
              {roChip(draft.smtp_verify_enabled, t("takedown.autopolicy.smtp_on"), t("takedown.autopolicy.smtp_off"), "info")}
            </Tooltip>
          </Stack>

          <Box sx={{ textAlign: "right", pt: 2 }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? t("takedown.autopolicy.saving") : t("takedown.autopolicy.save_button")}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AutoPolicyPanel;
